import axios from 'axios';
import Swal from 'sweetalert2';
import Cookies from 'js-cookie';
import { useTranslation } from 'react-i18next';
import HTMLReactParser from 'html-react-parser';
import React, { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
// api
import ApiKey from '../api/ApiKey';
import BaseApi from '../api/BaseApi';
// components
import Error from './Error';
import Footer from './Footer';
import NavBar from './NavBar';
import JobCard from './JobCard';
import ApplyJobModal from './ApplyJobModal';
// helpers
import { addBreadcrumbSchema, logOutIfTokenExpired, resultInfo } from '../helpers';
// mui
import Tooltip from '@mui/material/Tooltip';

const JobDescription = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { slug, catslug } = useParams();
  const { t } = useTranslation('global');
  const GUID = localStorage.getItem('GUID');
  const userType = Cookies.get('user_type');
  const tokenKey = Cookies.get('tokenClient');
  const userId = Cookies.get('user_id') || '';

  const params = new URLSearchParams(location.search);
  const openApplyModal = params.get('apply_modal');

  // const [error, setError] = useState(false);
  const [loading, setLoading] = useState(true);
  const [skillData, setSkillData] = useState([]);
  const [langsData, setLengsData] = useState([]);
  const [relevantJob, setRelevantJob] = useState([]);
  const [isNotFound, setIsNotFound] = useState(false);
  const [subCategoriesData, setSubCategoriesData] = useState([]);
  const [subCategoriesData2, setSubCategoriesData2] = useState([]);
  const [subCategoriesData3, setSubCategoriesData3] = useState([]);
  const [jobDescriptionData, setJobDescriptionData] = useState([]);
  // const [hideCompanyContact, setHideCompanyContact] = useState(true);
  const [hideVacancyContact, setHideVacancyContact] = useState(true);
  const [openJobDescrApplyJobModal, setOpenJobDescrApplyJobModal] = useState(false);

  const getData = async () => {
    try {
      setLoading(true);

      const response = await axios.post(
        BaseApi + `/candidates/apps_jobdetail/${slug}/${catslug}`,
        { guid: GUID, user_id: userId },
        {
          headers: {
            key: ApiKey,
            token: tokenKey,
            contentType: 'application/json',
          },
        },
      );

      setLoading(false);

      if (response.data.status === 200) {
        const {
          title,
          salary,
          job_type,
          location,
          posted_date,
          company_name,
          contact_name,
        } = response.data.response;

        const metaTitle = document.querySelector('title');
        const metaDescription = document.querySelector('meta[name="description"]');
        const metaTitleValue = `${title} - ${company_name} в місті ${location}, створена ${posted_date} | GRC.ua`; // eslint-disable-line
        const metaDescriptionValue = `${company_name} шукає ${title} у місті ${location}. Тип зайнятості - ${job_type}. Заробітна плата: ${salary}. Контактна особа - ${contact_name ? contact_name : 'не зазначено'}. Запропонуйте резюме на сайті GRC.ua`; // eslint-disable-line

        if (metaTitle) {
          metaTitle.textContent = metaTitleValue;
        }

        if (metaDescription) {
          metaDescription.content = metaDescriptionValue;
        }

        sessionStorage.removeItem('jobSearched');
        setJobDescriptionData(response.data.response);
        setLengsData(response.data.response.lang_skills);
        setSkillData(response.data.response.skills_array);
        setRelevantJob(response.data.response.relevantJobList);
        setSubCategoriesData(response.data.response.sub_categories);
        setSubCategoriesData2(response.data.response.sub_categories_2);
        setSubCategoriesData3(response.data.response.sub_categories_3);
      } else {
        resultInfo('error', response.data.message, t('jobDescription.close'));
      }
    } catch (error) {
      setLoading(false);

      if (error.message === 'Network Error' || error.response.data.message === 'Token has expired') {
        navigate('/');

        logOutIfTokenExpired(t('tokenExpired.tokenExpired'), t('tokenExpired.close'));
      } else if (error.response.status === 404) {
        setIsNotFound(true);
      }

      console.error('Cannot get job description through applied job section at job seeker', error);
    }
  };

  const getAppliedData = async id => {
    try {
      setLoading(true);

      await axios.post(
        BaseApi + `/job/applypop/${id}`,
        null,
        {
          headers: {
            key: ApiKey,
            token: tokenKey,
            contentType: 'application/json',
          },
        },
      );

      setLoading(false);
    } catch (error) {
      setLoading(false);

      console.error('Error at applied button at job description');
    }
  };

  const savedJob = async slug => {
    try {
      setLoading(true);

      const response = await axios.post(
        BaseApi + `/job/JobSave/${slug}`,
        null,
        {
          headers: {
            key: ApiKey,
            token: tokenKey,
            contentType: 'application/json',
          },
        },
      );

      setLoading(false);

      if (response.data.status === 200) {
        getData();

        resultInfo('success', t('jobDescription.savedJobSuccessTitle'), t('jobDescription.close'));
      } else {
        resultInfo('error', response.data.message, t('jobDescription.close'));
      }
    } catch (error) {
      setLoading(false);

      resultInfo('error', t('jobDescription.savedJobFailedTitle'), t('jobDescription.close'));

      console.error('Cannot save job!', error);
    }
  };

  const handleClickWithoutLogin = () => {
    navigate('/user/jobseekerlogin');

    const Toast = Swal.mixin({
      timer: 3000,
      toast: true,
      position: 'top-end',
      timerProgressBar: true,
      showConfirmButton: false,
      didOpen: toast => {
        toast.addEventListener('mouseenter', Swal.stopTimer);
        toast.addEventListener('mouseleave', Swal.resumeTimer);
      },
    });

    Toast.fire({
      icon: 'warning',
      title: t('jobDescription.pleaseLogin'),
    });
  };

  useEffect(() => {
    const canonicalUrl = `${window.location.origin}${location.pathname}`;

    let linkElement = document.querySelector('link[rel="canonical"]');

    if (linkElement) {
      linkElement.setAttribute('href', canonicalUrl);
    } else {
      linkElement = document.createElement('link');
      linkElement.setAttribute('rel', 'canonical');
      linkElement.setAttribute('href', canonicalUrl);
      document.head.appendChild(linkElement);
    }

    const breadcrumbs = [
      { name: t('searchJobPage.searchJobs'), item: `https://${window.location.host}/searchjob` },
      { name: jobDescriptionData.category, item: `https://${window.location.host}/searchjob/c-${catslug}` },
      { name: jobDescriptionData.title },
    ];

    const script = addBreadcrumbSchema(breadcrumbs);

    return () => {
      if (script) document.head.removeChild(script);
    };
  }, [catslug, jobDescriptionData.category, jobDescriptionData.title, location.pathname, t]);

  useEffect(() => {
    getAppliedData();

    if (GUID) {
      getData();
    }

    if (userType !== 'recruiter' && openApplyModal) {
      setOpenJobDescrApplyJobModal(true);
    }
  }, [GUID]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    isNotFound
      ? (
        <Error />
      ) : (
        <>
          <NavBar />
          {
            loading
              ? (
                <div className='loader-container' />
              ) : (
                <>
                  <section className='JobDetailTopBx'>
                    <div className='container'>
                      <h6 className='text-muted fw-normal'>
                        <Link to='/' style={{ color: 'grey' }}>
                          {t('navHeaders.home')}
                        </Link>
                        {' / '}
                        <Link to='/searchjob' style={{ color: 'grey' }}>
                          {t('searchJobPage.searchJobs')}
                        </Link>
                        {' / '}
                        {
                          jobDescriptionData?.category
                            ? (
                              <Link to={`/searchjob/c-${catslug}`} style={{ color: 'grey' }}>
                                {HTMLReactParser(jobDescriptionData.category)}{' / '}
                              </Link>
                            )
                            : ''
                        }
                        {
                          jobDescriptionData?.title
                            ? HTMLReactParser(jobDescriptionData.title)
                            : ''
                        }
                      </h6>
                      <div className='row'>
                        <div className='col-lg-7 col-md-7'>
                          <div className='single-job-detail'>
                            <div className='logo-detail'>
                              <img
                                alt='Company logo'
                                className='JDImage'
                                src={
                                  jobDescriptionData?.logo?.endsWith('/') || !jobDescriptionData?.logo
                                    ? '/Images/jobseekerSide/dummy-profile.png'
                                    : jobDescriptionData.logo
                                }
                              />
                            </div>
                            <div className='job-meta-detail'>
                              <h1>
                                {
                                  jobDescriptionData.title
                                    ? HTMLReactParser(jobDescriptionData.title)
                                    : ''
                                }
                                {
                                  jobDescriptionData.status === 0 && (
                                    <span className='text-muted'>{` (${t('jobDescription.archiveVacancy')})`}</span>
                                  )
                                }
                              </h1>
                              <p>
                                <Tooltip title={t('jobDescription.experience')}>
                                  <i className='fa-sharp fa-solid fa-briefcase' />
                                </Tooltip>
                                {' '}
                                {
                                  jobDescriptionData.experience && jobDescriptionData.experience !== 'N/A'
                                    ? jobDescriptionData.experience
                                    : t('jobDescription.notAvailable')
                                }
                              </p>
                              <p>
                                <Tooltip title={t('jobDescription.location')}>
                                  <i className='fa-sharp fa-solid fa-location-dot' />
                                </Tooltip>
                                {' '}
                                {jobDescriptionData.location}
                              </p>
                              <p>
                                <Tooltip title={t('jobDescription.postedDate')}>
                                  <i className='fa-solid fa-calendar-days' />
                                </Tooltip>
                                {' '}
                                {jobDescriptionData.posted_date}
                              </p>
                            </div>
                          </div>
                        </div>
                        {
                          userType !== 'recruiter' && (
                            <div className='col-lg-5 col-md-5'>
                              <div className='JobDetailBtnBx'>
                                {
                                  !userType
                                    ? (
                                      <div className='ApplyJobBtnBx'>
                                        <button
                                          className='button1'
                                          onClick={() => setOpenJobDescrApplyJobModal(true)}
                                        >
                                          {t('jobDescription.applyNow')}
                                        </button>
                                        <button
                                          className='button2'
                                          onClick={() => handleClickWithoutLogin()}
                                        >
                                          <i className='fa fa-light fa-star-o' />
                                          {' '}
                                          {t('jobDescription.saveJob')}
                                        </button>
                                      </div>
                                    )
                                    : ''
                                }
                                {
                                  userType === 'candidate'
                                    ? (
                                      <div className='ApplyJobBtnBx'>
                                        {
                                          jobDescriptionData.is_applied === 1
                                            ? (
                                              <button
                                                disabled
                                                className='button1'
                                              >
                                                {t('jobDescription.applied')}
                                              </button>
                                            )
                                            : (
                                              <button
                                                type='button'
                                                className='button1'
                                                onClick={() => setOpenJobDescrApplyJobModal(true)}
                                              >
                                                {t('jobDescription.applyNow')}
                                              </button>
                                            )
                                        }
                                        {
                                          jobDescriptionData.is_saved === 1
                                            ? (
                                              <button
                                                disabled
                                                className='button2'
                                              >
                                                <i className='fa fa-light fa-star-o' />
                                                {' '}
                                                {t('jobDescription.saved')}
                                              </button>
                                            )
                                            : (
                                              <button
                                                className='button2'
                                                onClick={() => savedJob(slug)}
                                              >
                                                <i className='fa fa-light fa-star-o' />
                                                {' '}
                                                {t('jobDescription.saveJob')}
                                              </button>
                                            )
                                        }
                                      </div>
                                    )
                                    : ''
                                }
                              </div>
                            </div>
                          )
                        }
                      </div>
                    </div>
                  </section>
                  <section className='JobDetailsDescriptionSection'>
                    <div className='container JDSection2'>
                      <div className='row'>
                        <div className='col-lg-8 col-md-8'>
                          <div className='JobDescriptionBx standartListStyle mb-4 mb-md-0'>
                            <h2 className='mb-4'>{t('jobDescription.jobDescription')}</h2>
                            <p>
                              {
                                jobDescriptionData.description
                                  ? HTMLReactParser(jobDescriptionData.description)
                                  : ''
                              }
                            </p>
                            <h2 className='mt-5 mb-4'>{t('jobDescription.skillRequired')}</h2>
                            <div>
                              {
                                skillData.length > 0
                                  ? skillData.map((i, index) => (
                                    <p className='keySkillitem' key={index}>
                                      <i className='fa-solid fa-arrow-right' />
                                      {' '}
                                      {i}
                                    </p>
                                  ))
                                  : t('jobDescription.notAvailable')
                              }
                            </div>
                            {
                              langsData.length > 0 && (
                                <div>
                                  <h2 className='mt-5 mb-4'>{t('employerCreateJob.additionalLanguages')}</h2>
                                  {
                                    langsData.map((i, index) => (
                                      <p className='keySkillitem' key={index}>
                                        <i className='fa-solid fa-arrow-right' />
                                        {' '}
                                        {i}
                                      </p>
                                    ))
                                  }
                                </div>
                              )
                            }
                            {
                              jobDescriptionData?.form_link && (
                                <div>
                                  <h2 className='mt-5'>{t('jobDescription.vacancyContacts')}</h2>
                                  <div className='JobDetailsInfo'>
                                    {
                                      hideVacancyContact
                                        ? (
                                          <Link
                                            id='vacancyContacts'
                                            className='hideContact'
                                            onClick={() => setHideVacancyContact(false)}
                                          >
                                            {t('jobDescription.showVacancyContacts')}
                                          </Link>
                                        ) : (
                                          <ul>
                                            {/* <li>
                                            <span className='jobDescriptionKeys'>
                                              <i className='fa-solid fa-user' /> {t('jobDescription.contactName')}:{' '}
                                            </span>
                                            {
                                              jobDescriptionData.contact_name
                                                ? jobDescriptionData.contact_name
                                                : t('jobDescription.notAvailable')
                                            }
                                          </li>
                                          <li>
                                            <span className='jobDescriptionKeys'>
                                              <i className='fa-solid fa-envelope' /> {t('jobDescription.email')}:{' '}
                                            </span>
                                            {
                                              jobDescriptionData.contact_email
                                                ? (
                                                  <a href={`mailto:${jobDescriptionData.contact_email}`}>
                                                    {jobDescriptionData.contact_email}
                                                  </a>
                                                )
                                                : t('jobDescription.notAvailable')
                                            }
                                          </li>
                                          <li>
                                            <span className='jobDescriptionKeys'>
                                              <i className='fa-solid fa-phone' /> {t('jobDescription.contactNumber')}:{' '}
                                            </span>
                                            {
                                              jobDescriptionData.contact_number
                                                ? (
                                                  <a href={`tel:${jobDescriptionData.contact_number}`}>
                                                    {jobDescriptionData.contact_number}
                                                  </a>
                                                ) : (
                                                  t('jobDescription.notAvailable')
                                                )
                                            }
                                          </li>
                                          {
                                            jobDescriptionData.contact_number_2 && (
                                              <li>
                                                <span className='jobDescriptionKeys'>
                                                  <i className='fa-solid fa-phone' /> {t('jobDescription.contactNumber')}:{' '}
                                                </span>
                                                <a href={`tel:${jobDescriptionData.contact_number_2}`}>
                                                  {jobDescriptionData.contact_number_2}
                                                </a>
                                              </li>
                                            )
                                          }
                                          {
                                            jobDescriptionData.contact_number_3 && (
                                              <li>
                                                <span className='jobDescriptionKeys'>
                                                  <i className='fa-solid fa-phone' /> {t('jobDescription.contactNumber')}:{' '}
                                                </span>
                                                <a href={`tel:${jobDescriptionData.contact_number_3}`}>
                                                  {jobDescriptionData.contact_number_3}
                                                </a>
                                              </li>
                                            )
                                          } */}
                                            {
                                              jobDescriptionData.form_link && (
                                                <li>
                                                  <span className='jobDescriptionKeys'>
                                                    <i className='fas fa-external-link-alt' />{' '}
                                                    {t('jobDescription.formLink')}:{' '}
                                                  </span>
                                                  <a href={jobDescriptionData.form_link} target='blank'>
                                                    {jobDescriptionData.form_link}
                                                  </a>
                                                </li>
                                              )
                                            }
                                          </ul>
                                        )
                                    }
                                  </div>
                                </div>
                              )
                            }
                          </div>
                        </div>
                        <div className='col-lg-4 col-md-4'>
                          <div className='card Jcard JobDetailsCard'>
                            <div className='card-title'>
                              <h2>{t('jobDescription.jobOverview')}</h2>
                            </div>
                            <div className='card-body JcardBody'>
                              <div className='JobDetailsInfo'>
                                <ul>
                                  <li>
                                    <span className='jobDescriptionKeys'>
                                      <i className='fa-solid fa-info-circle' /> {t('jobDescription.vacancyID')}:{' '}
                                    </span>
                                    {jobDescriptionData.job_id && jobDescriptionData.job_id}
                                  </li>
                                  <li>
                                    <span className='jobDescriptionKeys'>
                                      <i className='fa-solid fa-calendar-days' /> {t('jobDescription.datePosted')}:{' '}
                                    </span>
                                    {jobDescriptionData.posted_date}
                                  </li>
                                  <li>
                                    <span className='jobDescriptionKeys'>
                                      <i className='fa-solid fa-sitemap' /> {t('jobDescription.category')}:{' '}
                                    </span>
                                    {
                                      jobDescriptionData.category
                                        ? (
                                          <p className='keySkillitem'>
                                            <i className='fa-solid fa-arrow-right'></i> {jobDescriptionData.category}
                                          </p>
                                        )
                                        : t('jobDescription.notAvailable')
                                    }
                                    {
                                      jobDescriptionData.category_2 && (
                                        <p className='keySkillitem'>
                                          <i className='fa-solid fa-arrow-right'></i> {jobDescriptionData.category_2}
                                        </p>
                                      )
                                    }
                                    {
                                      jobDescriptionData.category_3 && (
                                        <p className='keySkillitem'>
                                          <i className='fa-solid fa-arrow-right'></i> {jobDescriptionData.category_3}
                                        </p>
                                      )
                                    }
                                  </li>
                                  <li>
                                    <span className='jobDescriptionKeys'>
                                      <i className='fa-solid fa-sitemap' /> {t('jobDescription.subCategory')}:{' '}
                                    </span>
                                    {
                                      subCategoriesData?.length > 0
                                        ? subCategoriesData.map((i, index) => (
                                          i !== undefined && (
                                            <p className='keySkillitem' key={index}>
                                              <i className='fa-solid fa-arrow-right' />
                                              {' '}
                                              {i}
                                            </p>
                                          )
                                        ))
                                        : t('jobDescription.notAvailable')
                                    }
                                    {
                                      subCategoriesData2?.length > 0 && (
                                        subCategoriesData2.map((i, index) => (
                                          i !== undefined && (
                                            <p className='keySkillitem' key={index}>
                                              <i className='fa-solid fa-arrow-right' />
                                              {' '}
                                              {i}
                                            </p>
                                          )
                                        )))
                                    }
                                    {
                                      subCategoriesData3?.length > 0 && (
                                        subCategoriesData3.map((i, index) => (
                                          i !== undefined && (
                                            <p className='keySkillitem' key={index}>
                                              <i className='fa-solid fa-arrow-right' />
                                              {' '}
                                              {i}
                                            </p>
                                          )
                                        )))
                                    }
                                  </li>
                                  <li>
                                    <span className='jobDescriptionKeys'>
                                      <i className='fa-solid fa-clock' /> {t('jobDescription.jobType')}:{' '}
                                    </span>
                                    {jobDescriptionData.job_type}
                                  </li>
                                  <li>
                                    <span className='jobDescriptionKeys'>
                                      <i className='fa-solid fa-dollar-sign' />{' '}
                                      {t('jobDescription.salary')}:{' '}
                                    </span>
                                    {
                                      jobDescriptionData.salary !== 'N/A'
                                        ? jobDescriptionData.salary
                                        : t('jobDescription.notAvailable')
                                    }
                                  </li>
                                </ul>
                              </div>
                            </div>
                          </div>
                          <div className='card Jcard JobDetailsCard'>
                            <div className='card-title'>
                              <h2>{t('jobDescription.contactInfo')}</h2>
                            </div>
                            <div className='card-body JcardBody'>
                              <div className='JobDetailsInfo'>
                                <ul>
                                  <li>
                                    <span className='jobDescriptionKeys'>
                                      <i className='fa-solid fa-users' /> {t('jobDescription.companyName')}:{' '}
                                    </span>
                                    {
                                      jobDescriptionData.company_name
                                        ? (
                                          <Link to={`/company/${jobDescriptionData.employer_id}`}>
                                            {jobDescriptionData.company_name}
                                          </Link>
                                        )
                                        : t('jobDescription.notAvailable')
                                    }
                                  </li>
                                  <li>
                                    <span className='jobDescriptionKeys'>
                                      <i className='fa-solid fa-user' /> {t('jobDescription.recruiterName')}:{' '}
                                    </span>
                                    {
                                      jobDescriptionData.contact_name
                                        ? jobDescriptionData.contact_name
                                        : t('jobDescription.notAvailable')
                                    }
                                  </li>
                                  {/* <li>
                                  <span className='jobDescriptionKeys'>
                                    <i className='fa-solid fa-phone' /> {t('jobDescription.contactCompany')}:{' '}
                                  </span>
                                  {
                                    hideCompanyContact ? (
                                      <Link
                                        id='companyContacts'
                                        className='hideContact'
                                        onClick={() => setHideCompanyContact(false)}
                                      >
                                        {t('jobDescription.showContactCompany')}
                                      </Link>
                                    ) : (
                                      jobDescriptionData.contact_number
                                        ? (
                                          <a href={`tel:${jobDescriptionData.contact_number}`}>
                                            {jobDescriptionData.contact_number}
                                          </a>
                                        )
                                        : t('jobDescription.notAvailable')
                                    )
                                  }
                                </li> */}
                                  <li>
                                    <span className='jobDescriptionKeys'>
                                      <i className='fa-solid fa-share' /> {t('jobDescription.website')}:{' '}
                                    </span>
                                    {
                                      jobDescriptionData.website && jobDescriptionData.website !== 'N/A'
                                        ? (
                                          <a href={jobDescriptionData.website} target='blank'>
                                            {jobDescriptionData.website}
                                          </a>
                                        )
                                        : t('jobDescription.notAvailable')
                                    }
                                  </li>
                                </ul>
                              </div>
                            </div>
                          </div>
                        </div>
                        <div className='JobDetailBtnBx'>
                          {
                            !userType
                              ? (
                                <div className='ApplyJobBtnBx' style={{ justifyContent: 'start' }}>
                                  <button
                                    className='btn button1'
                                    onClick={() => setOpenJobDescrApplyJobModal(true)}
                                  >
                                    {t('jobDescription.applyNow')}
                                  </button>
                                  <button
                                    className='btn button2'
                                    onClick={() => handleClickWithoutLogin()}
                                  >
                                    <i className='fa fa-light fa-star-o' />
                                    {' '}
                                    {t('jobDescription.saveJob')}
                                  </button>
                                </div>
                              )
                              : ''
                          }
                          {
                            userType === 'candidate'
                              ? (
                                <div className='ApplyJobBtnBx' style={{ justifyContent: 'start' }}>
                                  {
                                    jobDescriptionData.is_applied === 1
                                      ? (
                                        <button
                                          disabled
                                          className='btn button1'
                                        >
                                          {t('jobDescription.applied')}
                                        </button>
                                      )
                                      : (
                                        <button
                                          type='button'
                                          className='btn button1'
                                          onClick={() => setOpenJobDescrApplyJobModal(true)}
                                        >
                                          {t('jobDescription.applyNow')}
                                        </button>
                                      )
                                  }
                                  {
                                    jobDescriptionData.is_saved === 1
                                      ? (
                                        <button
                                          disabled
                                          className='btn button2'
                                        >
                                          <i className='fa fa-light fa-star-o' />
                                          {' '}
                                          {t('jobDescription.saved')}
                                        </button>
                                      )
                                      : (
                                        <button
                                          className='btn button2'
                                          onClick={() => savedJob(slug)}
                                        >
                                          <i className='fa fa-light fa-star-o' />
                                          {' '}
                                          {t('jobDescription.saveJob')}
                                        </button>
                                      )
                                  }
                                </div>
                              )
                              : ''
                          }
                        </div>
                        {
                          relevantJob.length > 0 && (
                            <div className='similarJobsSection'>
                              <h2 className='mt-md-4'>{t('jobDescription.similarJobs')}</h2>
                              <div className='row'>
                                {
                                  relevantJob.map(i => (
                                    <div key={i.id} className='mt-4 col-lg-4 col-md-4'>
                                      <JobCard
                                        logo={i.logo}
                                        slug={i.slug}
                                        title={i.title}
                                        jobId={i.job_id}
                                        getData={getData}
                                        created={i.created}
                                        min_exp={i.min_exp}
                                        cat_slug={i.cat_slug}
                                        job_city={i.job_city}
                                        setLoading={setLoading}
                                        work_type={i.work_type}
                                        is_applied={i.is_applied}
                                        salary_max={i.salary_max}
                                        salary_min={i.salary_min}
                                        onlyResume={i.only_resume}
                                        company_name={i.company_name}
                                        onlyQuestionnaire={i.questionary}
                                        salary_currency={i.salary_currency}
                                      />
                                    </div>
                                  ))
                                }
                              </div>
                              <div className='text-end'>
                                <Link to={`/searchjob/c-${relevantJob[0].cat_slug}`}>
                                  <button className='btn button1 mt-3'>
                                    {t('jobDescription.showMore')}
                                  </button>
                                </Link>
                              </div>
                            </div>
                          )
                        }
                      </div>
                    </div>
                  </section>
                  <Footer />
                  <ApplyJobModal
                    open={openJobDescrApplyJobModal}
                    jobId={jobDescriptionData.job_id}
                    jobTitle={jobDescriptionData.title}
                    onlyResume={jobDescriptionData.only_resume}
                    onlyQuestionnaire={jobDescriptionData.questionary}
                    onClose={() => {
                      setOpenJobDescrApplyJobModal(false);
                      getData();
                    }}
                    stepFrom={userType === 'candidate' ? 'jobApplySelect' : 'checkUser'}
                  />
                </>
              )
          }
        </>
      )
  );
};

export default JobDescription;
